import React, { useState } from "react";
import useSWR, { mutate } from "swr";
import { api } from "@/lib/api";
import { formatIDR } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Target, Trash2 } from "lucide-react";
import { toast } from "sonner";

const fetcher = (url) => api.get(url).then((r) => r.data);

export default function Goals() {
  const { data: goals } = useSWR("/goals", fetcher);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [target, setTarget] = useState("");
  const [saved, setSaved] = useState("");
  const [deadline, setDeadline] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmId, setConfirmId] = useState(null);

  const create = async () => {
    if (!name.trim() || !target) return toast.error("Nama dan target harus diisi");
    try {
      await api.post("/goals", {
        name: name.trim(),
        target_amount: Number(target),
        saved_amount: Number(saved) || 0,
        deadline: deadline || null,
      });
      toast.success("Tujuan dibuat");
      mutate("/goals");
      setOpen(false); setName(""); setTarget(""); setSaved(""); setDeadline("");
    } catch { toast.error("Gagal membuat tujuan"); }
  };

  const updateSaved = async (id, current, delta) => {
    const next = Math.max(0, Number(current) + delta);
    try {
      await api.patch(`/goals/${id}`, { saved_amount: next });
      mutate("/goals");
    } catch { toast.error("Gagal update"); }
  };

  const del = async (id) => {
    try { await api.delete(`/goals/${id}`); mutate("/goals"); toast.success("Dihapus"); } catch {}
  };

  const handleConfirmDelete = () => {
    if (confirmId) {
      del(confirmId);
      setConfirmId(null);
    }
    setConfirmOpen(false);
  };

  return (
    <div className="animate-fade-up">
      <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-4">
        <div>
          <p className="label-caps">Aspiration</p>
          <h1 className="font-heading text-3xl md:text-4xl font-medium tracking-tight mt-1">Financial Goals</h1>
          <p className="text-sm text-muted-foreground mt-2">Susun target menabung dan pantau progresnya.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-foreground text-background hover:bg-foreground/90 h-11" data-testid="add-goal-btn">
              <Plus className="h-4 w-4 mr-2" /> Tujuan Baru
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">Tujuan Baru</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div><Label>Nama</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="mt-2 h-11" placeholder="Liburan Bali, DP Rumah…" data-testid="goal-name-input" /></div>
              <div><Label>Target (IDR)</Label><Input type="number" value={target} onChange={(e) => setTarget(e.target.value)} className="mt-2 h-11 font-mono" data-testid="goal-target-input" /></div>
              <div><Label>Sudah Ditabung (IDR)</Label><Input type="number" value={saved} onChange={(e) => setSaved(e.target.value)} className="mt-2 h-11 font-mono" data-testid="goal-saved-input" /></div>
              <div><Label>Deadline</Label><Input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} className="mt-2 h-11" data-testid="goal-deadline-input" /></div>
            </div>
            <DialogFooter><Button onClick={create} className="bg-foreground text-background" data-testid="create-goal-submit">Simpan</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
        {(goals || []).map((g) => {
          const pct = Math.min(100, Math.round((g.saved_amount / g.target_amount) * 100));
          return (
            <div key={g.id} className="border border-border bg-card p-5" data-testid={`goal-card-${g.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <Target className="h-5 w-5 text-ai" />
                  <div>
                    <p className="font-heading text-lg">{g.name}</p>
                    <p className="text-xs text-muted-foreground">Deadline: {g.deadline || "—"}</p>
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => { setConfirmId(g.id); setConfirmOpen(true); }} data-testid={`goal-delete-${g.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
              <div className="mt-5">
                <div className="flex items-baseline justify-between text-sm mb-2">
                  <span className="font-mono">{formatIDR(g.saved_amount)}</span>
                  <span className="text-muted-foreground font-mono">/ {formatIDR(g.target_amount)}</span>
                </div>
                <Progress value={pct} className="h-2" />
                <p className="text-xs text-muted-foreground mt-2">{pct}% tercapai</p>
              </div>
              <div className="flex gap-2 mt-4">
                <Button size="sm" variant="outline" onClick={() => updateSaved(g.id, g.saved_amount, 100000)} data-testid={`goal-add-100k-${g.id}`}>+ Rp 100rb</Button>
                <Button size="sm" variant="outline" onClick={() => updateSaved(g.id, g.saved_amount, 500000)} data-testid={`goal-add-500k-${g.id}`}>+ Rp 500rb</Button>
                <Button size="sm" variant="outline" onClick={() => updateSaved(g.id, g.saved_amount, 1000000)} data-testid={`goal-add-1m-${g.id}`}>+ Rp 1jt</Button>
              </div>
            </div>
          );
        })}
        {(!goals || goals.length === 0) && (
          <div className="col-span-full border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
            Belum ada tujuan. Buat tujuan pertamamu di atas.
          </div>
        )}
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Konfirmasi Hapus</DialogTitle>
          </DialogHeader>
          <div className="py-4 text-sm text-muted-foreground">
            Apakah Anda yakin ingin menghapus tujuan keuangan ini?
          </div>
          <DialogFooter className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>Batal</Button>
            <Button variant="destructive" className="bg-loss text-white hover:bg-loss/90" onClick={handleConfirmDelete}>Hapus</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
